import React, { useRef } from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout/Layout";
import Section from "../components/section/Section";
import ClinicHeader from "../components/clinic/ClinicHeader";
import ClinicDetails from "../components/clinic/ClinicDetails";
import ClinicBookingForm from "../components/clinic/ClinicBookingform";
import Grid from "../components/grid/Grid";
import ArticleCard from "../components/article-card/ArticleCard";
import TeamMemberThumbnail from "../components/team-member-thumbnail/TeamMemberThumbnail";
import FeatureSection from "../components/feature-section/FeatureSection";
import ClinicRateCard from "../components/clinic/ClinicRateCard";
import ButtonLink from "../components/links/ButtonLink";
import cssClass from "./clinic.module.scss";

const Clinic = ({ data, location }) => {
  const {
    name,
    slug,
    image,
    introduction,
    about,
    aboutImage,
    team,
    articles,
    rates,
    metaDescription,
  } = data.contentfulClinic;

  const {
    introductionTitle,
    meetClinicTitle,
    teamTitle,
    ratesTitle,
    ratesIntroduction,
    servicesTitle,
    bookingCallToActionTitle,
    bookAppointmentLink,
    getDirectionsLink,
  } = data.contentfulClinicPage;

  const seo = {
    title: name,
    description: metaDescription,
    image,
    url: location.href,
  };

  const formRef = useRef();
  const scrollToBookingForm = (e) => {
    formRef.current.scrollIntoView({ behavior: "smooth" });
    e.preventDefault();
  };

  return (
    <Layout seo={seo} navInvert>
      <ClinicHeader
        {...data.contentfulClinic}
        bookAppointmentLink={bookAppointmentLink}
        onLinkClick={scrollToBookingForm}
        getDirectionsLink={getDirectionsLink}
      />

      <Section className={cssClass.approachWrapperOuter} largeVerticalPadding>
        <Grid className={cssClass.approachWrapper}>
          <div className={cssClass.approachText}>
            <h2 className={cssClass.approachTitle}>{introductionTitle}</h2>
            <div
              dangerouslySetInnerHTML={{
                __html: introduction.childMarkdownRemark.html,
              }}
            />
          </div>
          <div className={cssClass.approachTeamMember}>
            <TeamMemberThumbnail
              key={team[0].id}
              {...team[0]}
              clinicSlug={slug}
              featured
            />
          </div>
        </Grid>
      </Section>

      <FeatureSection
        image={aboutImage}
        backgroundColor="var(--color-turquoise)"
      >
        <h1 className={cssClass.teamTitle}>{teamTitle}</h1>
        <div
          dangerouslySetInnerHTML={{ __html: about.childMarkdownRemark.html }}
          className={cssClass.clinicIntroText}
        />
      </FeatureSection>

      {team && (
        <Section verticalPadding>
          <h2 className={cssClass.meetClinicTitle}>{meetClinicTitle}</h2>
          <Grid horizontallyCenterItems columns={4} itemsArray={team}>
            {team.map((teamMember) => {
              return (
                <TeamMemberThumbnail
                  key={teamMember.id}
                  {...teamMember}
                  clinicSlug={slug}
                />
              );
            })}
          </Grid>
          {/* TODO: SHOW MORE */}
        </Section>
      )}

      <Section verticalPadding className={cssClass.detailsAndFormContainer}>
        <Grid className={cssClass.gridContainer}>
          <ClinicDetails
            className={cssClass.detailsContainer}
            image={data.contentfulClinic.image}
            {...data.contentfulClinic}
            {...data.contentfulClinicPage}
          />
          <aside className={cssClass.formContainer}>
            <div ref={formRef} className={cssClass.formStickyInnerContainer}>
              <ClinicBookingForm />
            </div>
          </aside>
        </Grid>
        <div className={cssClass.detailsAndFormContainerBackground} />
      </Section>

      {rates && (
        <Section className={cssClass.ourRates} verticalPadding>
          <h2>{ratesTitle}</h2>
          <div
            dangerouslySetInnerHTML={{
              __html: ratesIntroduction.childMarkdownRemark.html,
            }}
            className={cssClass.ourRatesIntroduction}
          />
          <Grid columns={4}>
            {rates.map((rate) => {
              return (
                <ClinicRateCard
                  title={rate.name}
                  priceFrom={rate.cost}
                  key={rate.id}
                />
              );
            })}
          </Grid>
        </Section>
      )}

      {articles && (
        <Section verticalPadding>
          <h2 className={cssClass.articlesTitle}>{servicesTitle}</h2>
          <Grid articleLayout>
            {articles.map((article, i) => {
              return (
                <ArticleCard
                  isRowLayout={i > 1}
                  key={article.id}
                  {...article}
                  folderPath={data.contentfulUnderstandFertilityPage.slug}
                />
              );
            })}
          </Grid>
        </Section>
      )}

      <div className={cssClass.bookingCTABackground}>
        <Section className={cssClass.bookingCTA}>
          <div className={cssClass.ctaRowContainer}>
            <h3>{bookingCallToActionTitle}</h3>
            <div>
              <ButtonLink
                onClick={scrollToBookingForm}
                alternativeInvert
                to="#booking-form"
                asAnchor
              >
                {bookAppointmentLink}
              </ButtonLink>
            </div>
          </div>
        </Section>
      </div>
    </Layout>
  );
};

export default Clinic;

export const query = graphql`
  query($slug: String!, $locale: String) {
    contentfulClinic(slug: { eq: $slug }, node_locale: { eq: $locale }) {
      name
      slug
      phone
      email
      address {
        childMarkdownRemark {
          html
        }
      }
      location {
        lon
        lat
      }
      image {
        title
        fluid {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      introduction {
        childMarkdownRemark {
          html
        }
      }
      about {
        childMarkdownRemark {
          html
        }
      }
      aboutImage {
        title
        fluid {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      team {
        id
        name
        title
        image {
          title
          fluid {
            ...GatsbyContentfulFluid_withWebp
          }
        }
        slug
      }
      gallery {
        title
        fluid {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      articles {
        id
        name
        image {
          title
          fluid {
            ...GatsbyContentfulFluid_withWebp
          }
        }
        slug
      }
      pregnancyRateIvf
      pregnancyRateIvfIcsi
      testimonial
      treatments
      techniques
      otherServices
      spokenLanguages {
        id
        name
        image {
          title
          fluid {
            ...GatsbyContentfulFluid_withWebp
          }
        }
      }
      officeHours
      internationalPhone
      rates {
        id
        name
        cost
      }
      metaDescription
    }
    contentfulClinicPage(node_locale: { eq: $locale }) {
      introductionTitle
      meetClinicTitle
      teamTitle
      ratesTitle
      ratesIntroduction {
        childMarkdownRemark {
          html
        }
      }
      servicesTitle
      bookingCallToActionTitle
      bookAppointmentLink
      getDirectionsLink
      pregnancyRatesTitle
      pregnancyRatesSubtitle
      ivfTitle
      ivfAverage
      ivfIcsiTitle
      ivfIcsiAverage
      nationalAverageTitle
      nationalAverageExtra
      offeringsTitle
      treatmentsTitle
      techniquesTitle
      otherServicesTitle
      contactTitle
      locationTitle
      officeHoursTitle
      telephoneNumbersTitle
      emailTitle
    }
    contentfulUnderstandFertilityPage(node_locale: { eq: $locale }) {
      slug
    }
  }
`;
